import { SyntheticEvent } from "react";
import { Action, Store } from "redux";
import { SagaMiddleware, Task } from "redux-saga";

import { IDataReaderClient } from "@multiweb/clients/dataReader";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { FileNameParts, IBridge, ICryptographicInfo, IProxyBridge, ISignatureInfo, NAME } from "@multiweb/reactCore/bridge/api";
import { IAttrValue, ILanguageExtended, ILocalizationItem, IPublicAttributeInfo, IRunMethodResult, OWDataType } from "@multiweb/types/contracts";
import {
    CancellationToken,
    ConditionOperator,
    DateTimeFormatObject,
    EventCallback,
    ExperimentName,
    ExtendedPropertiesType,
    GetFormattedNumberStringParams,
    IAppConfiguration,
    IBigNumber,
    IDataEventsManager,
    IDefferredWindow,
    IEnumType,
    IMenuNavigationConfig,
    INumberFormatInfo,
    INumberFormatOptions,
    IOWObject,
    IOWObjectList,
    IParticipant,
    ITypeFactory,
    IVisible,
    IWorkflowMenuItem,
    RequestContext,
    RunStaticMethodParams,
    RunStaticMethodWithParamsConfig,
} from "@multiweb/types/multiweb";
import { ApplicationModule, IAppState, ObjectType, OpenEditWindowParams, ReactAction, ReactActionId, SagaWithReturnType } from "@multiweb/types/react";
import { Tail } from "@redux-saga/core/effects";
import { MockBridge } from "../mock";

class ProxyBridge implements IProxyBridge {
    private static readonly _LOGGER = LazyLoadingSectionLogger.create(LoggingSection.coreReact);

    /**
     * Výchozí implementace bridge, která se z proxy provolává.
     * Používá se pro pozdější nastavení výchozí implementace pomocí {@link resetToDefaultImpl}.
     * @private
     */
    private readonly _defaultBridgeImpl = new MockBridge();
    /**
     * Aktuálně používaná implementace bridge, kterou proxy provolává.
     * @private
     */
    private _bridgeImpl: IBridge;
    /**
     * Store pro přístup do stavu.
     * @private
     */
    private _store: Store<IAppState> | null = null;
    /**
     * Funkce pro spuštění ság.
     * @private
     */
    private _runSaga: SagaMiddleware["run"] | null = null;
    /**
     * Task běžící ságy, pokud zadaná implementace poskytuje ságu.
     * Slouží pro zastavení ságy předchozí implementace bridge při zadání nové implementace.
     * @private
     */
    private _sagaTask: Task | null = null;

    constructor() {
        this._bridgeImpl = this._defaultBridgeImpl;
    }

    public readonly setBridgeImpl = (bridgeImpl: IBridge): void => {
        this._bridgeImpl = bridgeImpl;
        if (this._store && this._runSaga) {
            this._bridgeImpl.setStore(this._store, this._runSaga);
        }

        if (this._sagaTask) {
            this._sagaTask.cancel();
            this._sagaTask = null;
        }

        if (bridgeImpl.module.saga) {
            if (this._runSaga) {
                this._sagaTask = this._runSaga(bridgeImpl.module.saga);
            } else {
                ProxyBridge._LOGGER.error("Provided bridge implementation has saga, but it cannot be run, because ProxyBridge was not initialized by setStore method.");
            }
        }
    };

    public readonly resetToDefaultImpl = (): void => {
        this.setBridgeImpl(this._defaultBridgeImpl);
    };

    public readonly setStore = (storeParam: Store<IAppState>, runSaga: SagaMiddleware["run"]): void => {
        this._store = storeParam;
        this._runSaga = runSaga;
        this._bridgeImpl.setStore(storeParam, runSaga);
    };

    public get module(): ApplicationModule<typeof NAME> {
        return this._bridgeImpl.module;
    }

    public readonly dispatchAction = (action: Action): void => {
        this._bridgeImpl.dispatchAction(action);
    };

    public readonly runSaga = <S extends SagaWithReturnType<any[]>>(saga: S, ...args: Parameters<S>): Task => {
        return this._bridgeImpl.runSaga(saga, ...args);
    };

    public readonly errorHandler = (error: unknown): void => {
        this._bridgeImpl.errorHandler(error);
    };

    public readonly getState = (): IAppState => {
        return this._bridgeImpl.getState();
    };

    public readonly useSelector = <Fn extends(state: IAppState, ...args: Tail<Parameters<Fn>>) => ReturnType<Fn>>(selector: Fn, ...args: Tail<Parameters<Fn>>): ReturnType<Fn> => {
        return this._bridgeImpl.useSelector(selector, ...args);
    };

    public readonly getDataEventsManager = (): IDataEventsManager | undefined => {
        return this._bridgeImpl.getDataEventsManager();
    };

    public readonly getDIContainer = (): ITypeFactory => {
        return this._bridgeImpl.getDIContainer();
    };

    public readonly generateUUID = (): string => {
        return this._bridgeImpl.generateUUID();
    };

    public readonly getAppConfiguration = (): IAppConfiguration => {
        return this._bridgeImpl.getAppConfiguration();
    };

    public readonly isExperimentEnabled = (experimentName: ExperimentName): boolean => {
        return this._bridgeImpl.isExperimentEnabled(experimentName);
    };

    public readonly createIsLocalStorageKeyLoadedActionPattern = (key: string): (action: Action) => boolean => {
        return this._bridgeImpl.createIsLocalStorageKeyLoadedActionPattern(key);
    };

    public readonly createBigNumberProxy = (value: string | number): IBigNumber => {
        return this._bridgeImpl.createBigNumberProxy(value);
    };

    public readonly openUrl = (url: string, target?: string): void => {
        return this._bridgeImpl.openUrl(url, target);
    };

    public readonly getNumberFormat = (): INumberFormatInfo => {
        return this._bridgeImpl.getNumberFormat();
    };

    public readonly getAttrValue = (value: any): IAttrValue => {
        return this._bridgeImpl.getAttrValue(value);
    };

    public readonly getAttributeInfo = (objectName: string, attributesToCheck: string[]): Promise<readonly IPublicAttributeInfo[]> => {
        return this._bridgeImpl.getAttributeInfo(objectName, attributesToCheck);
    };

    public readonly readAttributeValue = (attrValue: IAttrValue | ObjectType | null | undefined, explicitKnownTypes?: any[]): any => {
        return this._bridgeImpl.readAttributeValue(attrValue, explicitKnownTypes);
    };

    public readonly combineConditionsArray = (operator: ConditionOperator, conditions: string[]): string => {
        return this._bridgeImpl.combineConditionsArray(operator, conditions);
    };

    public readonly getDataReaderClient = (): IDataReaderClient => {
        return this._bridgeImpl.getDataReaderClient();
    };

    public readonly getActivityParticipants = (instanceId: string, objectList: IOWObjectList, activityId: string, requestContext?: RequestContext): Promise<IParticipant[]> => {
        return this._bridgeImpl.getActivityParticipants(instanceId, objectList, activityId, requestContext);
    };

    public readonly stopPropagation = (event: SyntheticEvent): void => {
        this._bridgeImpl.stopPropagation(event);
    };

    public readonly stopPropagationNative = (event: Event): void => {
        this._bridgeImpl.stopPropagationNative(event);
    };

    public readonly getDocumentDownloadUrlByObjectId = (dataObjectId: string, objectName: string, attributeName: string): Promise<string | null> => {
        return this._bridgeImpl.getDocumentDownloadUrlByObjectId(dataObjectId, objectName, attributeName);
    };

    public readonly runStaticMethod = ({ className, methodName, userLocKey, formId, requestContext }: RunStaticMethodParams, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> => {
        return this._bridgeImpl.runStaticMethod({ className, methodName, userLocKey, formId, requestContext }, cancellationToken);
    };

    public runStaticMethodWithParams(runStaticMethodWithParamsConfig: RunStaticMethodWithParamsConfig, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> {
        return this._bridgeImpl.runStaticMethodWithParams(runStaticMethodWithParamsConfig, cancellationToken);
    }

    public readonly createObjectList = (): IOWObjectList => {
        return this._bridgeImpl.createObjectList();
    };

    public readonly isWFDefinedToClass = (className: string, requestContext?: RequestContext, force: boolean = false): Promise<boolean> => {
        return this._bridgeImpl.isWFDefinedToClass(className, requestContext, force);
    };

    public readonly objectsWFMenu = (objectList: IOWObjectList, requestContext?: RequestContext): Promise<IWorkflowMenuItem[]> => {
        return this._bridgeImpl.objectsWFMenu(objectList, requestContext);
    };

    public readonly getInstance = (instanceId: string): IOWObject | null => {
        return this._bridgeImpl.getInstance(instanceId);
    };

    public readonly prepareDownload = (url: string): Promise<boolean> => {
        return this._bridgeImpl.prepareDownload(url);
    };

    public readonly getEnumData = (enumName: string): Promise<IEnumType> => {
        return this._bridgeImpl.getEnumData(enumName);
    };

    public readonly generateElementID = (): string => {
        return this._bridgeImpl.generateElementID();
    };

    public readonly logUserInteractionEvent = (event: Event): Promise<void> => {
        return this._bridgeImpl.logUserInteractionEvent(event);
    };

    public readonly getControlComponentVisibility = (componentId: string): boolean => {
        return this._bridgeImpl.getControlComponentVisibility(componentId);
    };

    public readonly addControlComponentVisibilityChangedListener = (componentId: string, listener: EventCallback<IVisible, boolean>): void => {
        this._bridgeImpl.addControlComponentVisibilityChangedListener(componentId, listener);
    };

    public readonly removeControlComponentVisibilityChangedListener = (componentId: string, listener: EventCallback<IVisible, boolean>): void => {
        this._bridgeImpl.removeControlComponentVisibilityChangedListener(componentId, listener);
    };

    public get decimalSeparator(): string {
        return this._bridgeImpl.decimalSeparator;
    }

    public get groupSeparator(): string {
        return this._bridgeImpl.groupSeparator;
    }

    public get msPerDay(): number {
        return this._bridgeImpl.msPerDay;
    }

    public readonly removeAccents = (text: string): string => {
        return this._bridgeImpl.removeAccents(text);
    };

    public readonly stripHTMLFromText = (text: string): string => {
        return this._bridgeImpl.stripHTMLFromText(text);
    };

    public readonly isToday = (date: Date): boolean => {
        return this._bridgeImpl.isToday(date);
    };

    public readonly dateToString = (date: Date, format?: string): string => {
        return this._bridgeImpl.dateToString(date, format);
    };

    public readonly getExtendedPropertiesFromElement = (element: HTMLElement): ExtendedPropertiesType => {
        return this._bridgeImpl.getExtendedPropertiesFromElement(element);
    };

    public readonly getDefaultNumberFormat = (dataType?: OWDataType | string): string => {
        return this._bridgeImpl.getDefaultNumberFormat(dataType);
    };

    public readonly getFormattedNumberString = (value: string | IBigNumber | number | null | undefined, params: GetFormattedNumberStringParams, formatedProviderKey?: string): string => {
        return this._bridgeImpl.getFormattedNumberString(value, params, formatedProviderKey);
    };

    public readonly isWholeNumberType = (dataType: OWDataType | string): boolean => {
        return this._bridgeImpl.isWholeNumberType(dataType);
    };

    public readonly getDefaultFormat = (dataType?: OWDataType | string): string => {
        return this._bridgeImpl.getDefaultFormat(dataType);
    };

    public readonly getTypeMinMax = (dataType: OWDataType | string): Record<"min" | "max", number> | null => {
        return this._bridgeImpl.getTypeMinMax(dataType);
    };

    public readonly getNumberToString = (numStr: string, params: INumberFormatOptions): string => {
        return this._bridgeImpl.getNumberToString(numStr, params);
    };

    public readonly removeFormatting = (value: string, groupSeparator: string, decimalSeparator: string): string => {
        return this._bridgeImpl.removeFormatting(value, groupSeparator, decimalSeparator);
    };

    public readonly compareCaseInsensitive = (value1: string | null | undefined, value2: string | null | undefined): boolean => {
        return this._bridgeImpl.compareCaseInsensitive(value1, value2);
    };

    public readonly isNullOrEmpty = (value: string | null | undefined): boolean => {
        return this._bridgeImpl.isNullOrEmpty(value);
    };

    public readonly stringToEnumValue = <T>(targetEnum: T, value: string, caseSensitive?: boolean): T[keyof T] | undefined => {
        return this._bridgeImpl.stringToEnumValue(targetEnum, value, caseSensitive);
    };

    public readonly flagToCss = (flag: string): string => {
        return this._bridgeImpl.flagToCss(flag);
    };

    public readonly valueToEnumValue = <T>(targetEnum: T, value: string, caseSensitive?: boolean): T[keyof T] | undefined => {
        return this._bridgeImpl.valueToEnumValue(targetEnum, value, caseSensitive);
    };

    public readonly getDateToStringFunction = (format: string, culture?: string): DateTimeFormatObject => {
        return this._bridgeImpl.getDateToStringFunction(format, culture);
    };

    public readonly loadLocalizationItems = (locKeys: readonly string[]): Promise<Map<string, ILocalizationItem>> => {
        return this._bridgeImpl.loadLocalizationItems(locKeys);
    };

    public readonly applyLocParams = (text: string, params?: string[]): string => {
        return this._bridgeImpl.applyLocParams(text, params);
    };

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public get actualCultureISO639_1(): string {
        return this._bridgeImpl.actualCultureISO639_1;
    }

    public readonly getSupportedLanguages = (): Promise<readonly ILanguageExtended[]> => {
        return this._bridgeImpl.getSupportedLanguages();
    };

    public readonly resetRefreshTimeout = (dataSourceKey: string): void => {
        this._bridgeImpl.resetRefreshTimeout(dataSourceKey);
    };

    public readonly executeReactConnectorAction = (dataSourceKey: string, actionId: ReactActionId, dataObjectId: string): void => {
        this._bridgeImpl.executeReactConnectorAction(dataSourceKey, actionId, dataObjectId);
    };

    public readonly openReactConnectorDetail = (dataSourceKey: string, dataObjectId: string): Promise<void> => {
        return this._bridgeImpl.openReactConnectorDetail(dataSourceKey, dataObjectId);
    };

    public readonly openAttributeEditWindow = (params: OpenEditWindowParams): Promise<void> => {
        return this._bridgeImpl.openAttributeEditWindow(params);
    };

    public readonly setReactConnectorMultiselectMode = (dataSourceKey: string, multiselectMode: boolean): void => {
        this._bridgeImpl.setReactConnectorMultiselectMode(dataSourceKey, multiselectMode);
    };

    public readonly getReactConnectorRowActions = (dataSourceKey: string): ReadonlyArray<ReactAction> => {
        return this._bridgeImpl.getReactConnectorRowActions(dataSourceKey);
    };

    public readonly getMenuNavigationConfig = (): IMenuNavigationConfig | undefined => {
        return this._bridgeImpl.getMenuNavigationConfig();
    };

    public readonly stringToBoolean = (value: string | null | undefined, defaultReturn?: boolean): boolean => {
        return this._bridgeImpl.stringToBoolean(value, defaultReturn);
    };

    public readonly stringToNumberOrUndefined = (value: string | undefined): number | undefined => {
        return this._bridgeImpl.stringToNumberOrUndefined(value);
    };

    public readonly splitFileName = (fileName: string): FileNameParts => {
        return this._bridgeImpl.splitFileName(fileName);
    };

    public readonly prettyPrintFileSize = (fileSize: number, locale?: string | string[]): string => {
        return this._bridgeImpl.prettyPrintFileSize(fileSize, locale);
    };

    public readonly isSignatureEmpty = (signatureInfo: ISignatureInfo): boolean => {
        return this._bridgeImpl.isSignatureEmpty(signatureInfo);
    };

    public readonly isSigned = (signatureInfo: ISignatureInfo): boolean => {
        return this._bridgeImpl.isSigned(signatureInfo);
    };

    public readonly isEncrypted = (cryptoInfo: ICryptographicInfo): boolean => {
        return this._bridgeImpl.isEncrypted(cryptoInfo);
    };

    public readonly hasSignatureWarning = (signatureInfo: ISignatureInfo, withCounterSignatures?: boolean): boolean => {
        return this._bridgeImpl.hasSignatureWarning(signatureInfo, withCounterSignatures);
    };

    public readonly isUrl = (value: string): boolean => {
        return this._bridgeImpl.isUrl(value);
    };

    public readonly isParametricUrl = (url: string): boolean => {
        return this._bridgeImpl.isParametricUrl(url);
    };

    public readonly isParametricMailto = (url: string): boolean => {
        return this._bridgeImpl.isParametricMailto(url);
    };

    public readonly isEmail = (value: string): boolean => {
        return this._bridgeImpl.isEmail(value);
    };

    public readonly isFile = (value: string): boolean => {
        return this._bridgeImpl.isFile(value);
    };

    public readonly hasParameters = (url: string): boolean => {
        return this._bridgeImpl.hasParameters(url);
    };

    public readonly replaceAll = (text: string, searchedString: string | RegExp, replaceValue: string): string => {
        return this._bridgeImpl.replaceAll(text, searchedString, replaceValue);
    };

    public readonly replaceAllWithLookahead = (text: string, searchedString: string, value: string, forbiddenString: string): string => {
        return this._bridgeImpl.replaceAllWithLookahead(text, searchedString, value, forbiddenString);
    };

    public readonly canCallOnlyStaticMethods = (url: string, functions: ReadonlyArray<string>): boolean => {
        return this._bridgeImpl.canCallOnlyStaticMethods(url, functions);
    };

    public readonly createDefferedWindow = (): IDefferredWindow => {
        return this._bridgeImpl.createDefferedWindow();
    };

    public readonly objectWFActionExecuteWithoutInstance = (ids: string[], className: string, actionID: string, nextParticipants: IParticipant[], requestContext?: RequestContext): Promise<IRunMethodResult> => {
        return this._bridgeImpl.objectWFActionExecuteWithoutInstance(ids, className, actionID, nextParticipants, requestContext);
    };
}

export const proxyBridgeInstance = new ProxyBridge();
